import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface MilestoneTemplate {
  id: string;
  sales_item_id: string;
  name: string;
  description?: string;
  milestone_order: number;
  estimated_duration_days?: number;
  auto_complete_on_event_code?: string;
  auto_complete_on_task_code?: string;
  created_at: string;
}

export const useMilestoneTemplates = (salesItemId?: string) => {
  const queryClient = useQueryClient();

  const { data: templates, isLoading } = useQuery({
    queryKey: ['milestone-templates', salesItemId],
    queryFn: async () => {
      let query = supabase
        .from('milestone_templates')
        .select('*')
        .order('milestone_order');

      if (salesItemId) {
        query = query.eq('sales_item_id', salesItemId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as MilestoneTemplate[];
    },
  });

  const createTemplate = useMutation({
    mutationFn: async (template: any) => {
      const { data, error } = await supabase
        .from('milestone_templates')
        .insert([template])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['milestone-templates'] });
      toast.success('Milestone template created successfully');
    },
    onError: (error) => {
      toast.error('Error creating milestone template: ' + error.message);
    },
  });

  const updateTemplate = useMutation({
    mutationFn: async ({ id, ...updates }: any) => {
      const { data, error } = await supabase
        .from('milestone_templates')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['milestone-templates'] });
      toast.success('Milestone template updated successfully');
    },
    onError: (error) => {
      toast.error('Error updating milestone template: ' + error.message);
    },
  });

  const deleteTemplate = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('milestone_templates')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['milestone-templates'] });
      toast.success('Milestone template deleted successfully');
    },
    onError: (error) => {
      toast.error('Error deleting milestone template: ' + error.message);
    },
  });

  return {
    templates,
    isLoading,
    createTemplate,
    updateTemplate,
    deleteTemplate,
  };
};
